import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import CachedIcon from '@material-ui/icons/Cached';
import { useFormik } from 'formik';
import * as yup from 'yup';
import moment from 'moment';
import { connect } from 'react-redux';
import { Spinner } from '../../components/UI';
import { CustomTextField } from '../../components/Textfield';
import { CustomDatePicker } from '../../components/DateTimePicker';
import {
    fetchAppConfig,
    getRerunHistory,
    rerunDateRangeRule,
    rerunWeeklyRule,
} from '../../store/actions/authActionCreator';
import { showMessage } from '../../utils/helper';

const DATE_FORMAT = 'YYYYMMDD';

// Accepts a single id or a comma-separated list, e.g. "123" or "123, 456, 789"
const ID_LIST_REGEX = /^\s*\d+\s*(,\s*\d+\s*)*$/;

const parseIdList = (value) =>
    (value ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
        .map(Number);

const idListField = (label) =>
    yup
        .string()
        .required(`${label} is required`)
        .test(
            'is-id-list',
            `${label} must be a number, or comma-separated numbers`,
            (value) => ID_LIST_REGEX.test(value ?? ''),
        );

const dailyValidationSchema = yup.object({
    userId: idListField('User Id'),
    locationId: idListField('Location Id'),
    startDateEpochMillis: yup.number().required('Start date is required'),
    endDateEpochMillis: yup
        .number()
        .required('End date is required')
        .test(
            'is-after-start',
            'End date must be after start date',
            function (value) {
                const { startDateEpochMillis } = this.parent;
                return (
                    !startDateEpochMillis ||
                    !value ||
                    value > startDateEpochMillis
                );
            },
        ),
});

const weeklyValidationSchema = yup.object({
    userId: idListField('User Id'),
    locationId: idListField('Location Id'),
    weekLastDayEpochMillis: yup.number().required('Week last day is required'),
});

function submitErrorMessage(error) {
    return typeof error === 'string' && error
        ? error
        : 'Something went wrong while submitting the rerun request';
}

function RerunHistoryTable({ logs, loading, onReload }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    return (
        <Paper
            elevation={2}
            style={{
                flex: '1 1 560px',
                minWidth: 0,
                padding: '24px',
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                }}
            >
                <Typography
                    variant="h6"
                    style={{ fontWeight: 700, color: '#14112d' }}
                >
                    Rerun History
                </Typography>
                <Tooltip title="Reload" placement="top">
                    <span>
                        <IconButton onClick={onReload} disabled={loading}>
                            <CachedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <TableContainer style={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ fontWeight: 700 }}>
                                User Id(s)
                            </TableCell>
                            <TableCell style={{ fontWeight: 700 }}>
                                Location Id(s)
                            </TableCell>
                            <TableCell style={{ fontWeight: 700 }}>
                                Start Date
                            </TableCell>
                            <TableCell style={{ fontWeight: 700 }}>
                                End Date
                            </TableCell>
                            <TableCell style={{ fontWeight: 700 }}>
                                Remarks
                            </TableCell>
                            <TableCell style={{ fontWeight: 700 }}>
                                Requested By (User Id)
                            </TableCell>
                            <TableCell style={{ fontWeight: 700 }}>
                                Requested At
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Box
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 0',
                                        }}
                                    >
                                        <CircularProgress size={18} />
                                        <Typography variant="body2">
                                            Loading...
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && logs?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No rerun requests yet.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            logs
                                ?.slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage,
                                )
                                ?.map((log) => (
                                    <TableRow key={log?.id}>
                                        <TableCell>{log?.userIds}</TableCell>
                                        <TableCell>
                                            {log?.locationIds}
                                        </TableCell>
                                        <TableCell>
                                            {log?.startDate
                                                ? moment(log.startDate).format(
                                                      'DD MMM YYYY',
                                                  )
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            {log?.endDate
                                                ? moment(log.endDate).format(
                                                      'DD MMM YYYY',
                                                  )
                                                : ''}
                                        </TableCell>
                                        <TableCell>
                                            {log?.remarks ?? ''}
                                        </TableCell>
                                        <TableCell>{log?.actorId}</TableCell>
                                        <TableCell>
                                            {log?.createdAt
                                                ? moment(log.createdAt).format(
                                                      'DD MMM YYYY, hh:mm A',
                                                  )
                                                : ''}
                                        </TableCell>
                                    </TableRow>
                                ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={logs?.length ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={(event, newPage) => setPage(newPage)}
                onChangeRowsPerPage={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                }}
            />
        </Paper>
    );
}

function DailyRerunForm({ enableRerun, dispatchRerunDateRangeRule, onDone }) {
    const [loading, toggleLoading] = useState(false);
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
    const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

    const formik = useFormik({
        initialValues: {
            userId: '',
            locationId: '',
            startDateEpochMillis: null,
            endDateEpochMillis: null,
            remarks: '',
        },
        validationSchema: dailyValidationSchema,
        onSubmit: async (values) => {
            toggleLoading(true);
            dispatchRerunDateRangeRule(
                {
                    userIds: parseIdList(values.userId),
                    locationIds: parseIdList(values.locationId),
                    startDateYYYYMMDD: moment(
                        values.startDateEpochMillis,
                    ).format(DATE_FORMAT),
                    endDateYYYYMMDD: moment(values.endDateEpochMillis).format(
                        DATE_FORMAT,
                    ),
                    remarks: values.remarks,
                },
                () => {
                    toggleLoading(false);
                    showMessage(
                        'Rerun request submitted successfully',
                        'success',
                    );
                    formik.resetForm();
                    onDone?.();
                },
                (error) => {
                    toggleLoading(false);
                    showMessage(submitErrorMessage(error), 'error');
                },
            );
        },
    });

    return (
        <Spinner loading={loading}>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            label="User Id(s)"
                            placeholder="e.g. 123 or 123, 456, 789"
                            variant="outlined"
                            id="userId"
                            name="userId"
                            value={formik.values.userId}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.userId
                                    ? Boolean(formik.errors.userId)
                                    : false
                            }
                            helperText={
                                formik.touched.userId && formik.errors.userId
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            label="Location Id(s)"
                            placeholder="e.g. 123 or 123, 456, 789"
                            variant="outlined"
                            id="locationId"
                            name="locationId"
                            value={formik.values.locationId}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.locationId
                                    ? Boolean(formik.errors.locationId)
                                    : false
                            }
                            helperText={
                                formik.touched.locationId &&
                                formik.errors.locationId
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Start Date"
                            value={formik.values.startDateEpochMillis}
                            onChange={(date) => {
                                formik.setFieldValue(
                                    'startDateEpochMillis',
                                    date ? moment(date).valueOf() : null,
                                    true,
                                );
                            }}
                            open={startDatePickerOpen}
                            onOpen={() => setStartDatePickerOpen(true)}
                            onClose={() => setStartDatePickerOpen(false)}
                            showInput={true}
                            error={
                                formik.touched.startDateEpochMillis
                                    ? Boolean(
                                          formik.errors.startDateEpochMillis,
                                      )
                                    : false
                            }
                            helperText={
                                formik.touched.startDateEpochMillis &&
                                formik.errors.startDateEpochMillis
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="End Date"
                            value={formik.values.endDateEpochMillis}
                            minDate={formik.values.startDateEpochMillis}
                            onChange={(date) => {
                                formik.setFieldValue(
                                    'endDateEpochMillis',
                                    date ? moment(date).valueOf() : null,
                                    true,
                                );
                            }}
                            open={endDatePickerOpen}
                            onOpen={() => setEndDatePickerOpen(true)}
                            onClose={() => setEndDatePickerOpen(false)}
                            showInput={true}
                            error={
                                formik.touched.endDateEpochMillis
                                    ? Boolean(formik.errors.endDateEpochMillis)
                                    : false
                            }
                            helperText={
                                formik.touched.endDateEpochMillis &&
                                formik.errors.endDateEpochMillis
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            label="Remarks"
                            placeholder="Optional notes about this rerun"
                            variant="outlined"
                            multiline={true}
                            rows={2}
                            id="remarks"
                            name="remarks"
                            value={formik.values.remarks}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                </Grid>

                <Typography
                    variant="caption"
                    style={{
                        color: '#757575',
                        display: 'block',
                        marginTop: '4px',
                    }}
                >
                    Note: End date is excluded from the rerun range.
                </Typography>

                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '24px',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!enableRerun}
                        disableElevation
                        style={{
                            fontWeight: 600,
                            padding: '8px 32px',
                        }}
                    >
                        Submit
                    </Button>
                </Box>
            </form>
        </Spinner>
    );
}

function WeeklyRerunForm({ enableRerun, dispatchRerunWeeklyRule }) {
    const [loading, toggleLoading] = useState(false);
    const [weekLastDayPickerOpen, setWeekLastDayPickerOpen] = useState(false);

    const formik = useFormik({
        initialValues: {
            userId: '',
            locationId: '',
            weekLastDayEpochMillis: null,
            remarks: '',
        },
        validationSchema: weeklyValidationSchema,
        onSubmit: async (values) => {
            toggleLoading(true);
            dispatchRerunWeeklyRule(
                {
                    userIds: parseIdList(values.userId),
                    locationIds: parseIdList(values.locationId),
                    weekLastDayYYYYMMDD: moment(
                        values.weekLastDayEpochMillis,
                    ).format(DATE_FORMAT),
                    remarks: values.remarks,
                },
                () => {
                    toggleLoading(false);
                    showMessage(
                        'Weekly rerun request submitted successfully',
                        'success',
                    );
                    formik.resetForm();
                },
                (error) => {
                    toggleLoading(false);
                    showMessage(submitErrorMessage(error), 'error');
                },
            );
        },
    });

    return (
        <Spinner loading={loading}>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            label="User Id(s)"
                            placeholder="e.g. 123 or 123, 456, 789"
                            variant="outlined"
                            id="userId"
                            name="userId"
                            value={formik.values.userId}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.userId
                                    ? Boolean(formik.errors.userId)
                                    : false
                            }
                            helperText={
                                formik.touched.userId && formik.errors.userId
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField
                            label="Location Id(s)"
                            placeholder="e.g. 123 or 123, 456, 789"
                            variant="outlined"
                            id="locationId"
                            name="locationId"
                            value={formik.values.locationId}
                            onChange={formik.handleChange}
                            error={
                                formik.touched.locationId
                                    ? Boolean(formik.errors.locationId)
                                    : false
                            }
                            helperText={
                                formik.touched.locationId &&
                                formik.errors.locationId
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            label="Week Last Day"
                            value={formik.values.weekLastDayEpochMillis}
                            onChange={(date) => {
                                formik.setFieldValue(
                                    'weekLastDayEpochMillis',
                                    date ? moment(date).valueOf() : null,
                                    true,
                                );
                            }}
                            open={weekLastDayPickerOpen}
                            onOpen={() => setWeekLastDayPickerOpen(true)}
                            onClose={() => setWeekLastDayPickerOpen(false)}
                            showInput={true}
                            error={
                                formik.touched.weekLastDayEpochMillis
                                    ? Boolean(
                                          formik.errors.weekLastDayEpochMillis,
                                      )
                                    : false
                            }
                            helperText={
                                formik.touched.weekLastDayEpochMillis &&
                                formik.errors.weekLastDayEpochMillis
                            }
                            isRequired={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            label="Remarks"
                            placeholder="Optional notes about this rerun"
                            variant="outlined"
                            multiline={true}
                            rows={2}
                            id="remarks"
                            name="remarks"
                            value={formik.values.remarks}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                </Grid>

                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '24px',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!enableRerun}
                        disableElevation
                        style={{
                            fontWeight: 600,
                            padding: '8px 32px',
                        }}
                    >
                        Submit
                    </Button>
                </Box>
            </form>
        </Spinner>
    );
}

function AmzRerun({
    user,
    dispatchFetchAppConfig,
    dispatchRerunDateRangeRule,
    dispatchRerunWeeklyRule,
    dispatchGetRerunHistory,
}) {
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [frequency, setFrequency] = useState('daily');
    const enableRerun = !!user?.config?.enableRerun;

    const fetchHistory = () => {
        setHistoryLoading(true);
        dispatchGetRerunHistory(
            (response) => {
                setHistoryLoading(false);
                setHistoryLogs(response?.response?.logs ?? []);
            },
            () => {
                setHistoryLoading(false);
            },
        );
    };

    useEffect(() => {
        dispatchFetchAppConfig(
            () => {},
            (error) => {
                showMessage(
                    typeof error === 'string' && error
                        ? error
                        : 'Failed to fetch app config',
                    'error',
                );
            },
        );
        fetchHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Paper style={{ padding: '24px' }} elevation={0}>
            <Box
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: '24px',
                }}
            >
                <Paper
                    elevation={2}
                    style={{
                        flex: '0 1 480px',
                        minWidth: 0,
                        padding: '32px',
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px',
                        }}
                    >
                        <Typography
                            variant="h6"
                            style={{ fontWeight: 700, color: '#14112d' }}
                        >
                            AMZ Rerun
                        </Typography>
                        <ToggleButtonGroup
                            value={frequency}
                            exclusive
                            size="small"
                            onChange={(event, value) =>
                                value && setFrequency(value)
                            }
                        >
                            <ToggleButton value="daily">Daily</ToggleButton>
                            <ToggleButton value="weekly">Weekly</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: '24px' }}
                    >
                        {frequency === 'daily'
                            ? 'Rerun the rate rule engine for one or more users / locations over a date range.'
                            : 'Rerun the weekly rate rule engine for one or more users / locations, anchored on the last day of the target week.'}
                    </Typography>

                    {frequency === 'daily' ? (
                        <DailyRerunForm
                            enableRerun={enableRerun}
                            dispatchRerunDateRangeRule={
                                dispatchRerunDateRangeRule
                            }
                            onDone={fetchHistory}
                        />
                    ) : (
                        <WeeklyRerunForm
                            enableRerun={enableRerun}
                            dispatchRerunWeeklyRule={dispatchRerunWeeklyRule}
                        />
                    )}
                </Paper>

                <RerunHistoryTable
                    logs={historyLogs}
                    loading={historyLoading}
                    onReload={fetchHistory}
                />
            </Box>
        </Paper>
    );
}

const mapStateToProps = (state) => ({
    user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchAppConfig: (onSuccess, onError) =>
        dispatch(fetchAppConfig(onSuccess, onError)),
    dispatchRerunDateRangeRule: (data, onSuccess, onError) =>
        dispatch(rerunDateRangeRule(data, onSuccess, onError)),
    dispatchRerunWeeklyRule: (data, onSuccess, onError) =>
        dispatch(rerunWeeklyRule(data, onSuccess, onError)),
    dispatchGetRerunHistory: (onSuccess, onError) =>
        dispatch(getRerunHistory(onSuccess, onError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AmzRerun);
